<template>
    <div class="vertical-bar-chart flex-row">
        <div class="wrapper wp-8">
            <!-- Custom label -->
            <div class="bar-labels-avatars flex-column jc-center" :height="heightBarChart">
                <div v-for="(id, index) in data.labels" :key="index" class="avatars-wrapper">
                    <el-tooltip
                        :content="avatar(id).fullname"
                        placement="bottom-start"
                        popperClass="horizontal-bar-chart-tool-tip"
                    >
                        <avatar :avatar="avatar(id)" size="small" />
                    </el-tooltip>
                </div>
            </div>
            <div>
                <Bar
                    ref="barChart"
                    class="bar-chart wp-10"
                    :data="chartData"
                    :options="options"
                    style="position: relative"
                />
            </div>
        </div>
        <!--         Custom legend -->
        <div class="bar-legend wp-2 m-4">
            <div v-if="isChartForLabel">
                <p class="fs-3 mb-1">{{ $l10n.__('Sélectionner une activité') }}</p>
                <el-select
                    v-model="activitySelected"
                    class="mb-3"
                    @change="onChangeActivitySelected"
                >
                    <el-option
                        v-for="activity in activities"
                        :key="activity.value"
                        :label="activity.title"
                        :value="activity.value"
                    ></el-option>
                </el-select>
            </div>
            <p class="semibold mt-2 mb-3 fs-3">
                <span v-if="isChartForLabel">{{ $l10n.__('Sélection des labels') }}</span>
                <span v-else>{{ $l10n.__('Sélection des activités') }}</span>
            </p>
            <el-checkbox-group v-model="checkbox" class="flex-column list-label">
                <el-checkbox
                    v-for="(dataset, index) in data.datasets"
                    :key="index"
                    :label="index"
                    class="mb-3"
                    @change="(isChecked) => handleFilterLegendChange(isChecked, index)"
                >
                    <chip-label
                        :color="dataset.backgroundColor"
                        :label="dataset.label"
                        gap="small"
                    />
                </el-checkbox>
            </el-checkbox-group>
        </div>
    </div>
</template>
<script>
import ChipLabel from 'vcomponents/utilities/ChipLabel.vue';
import Avatar from 'vcomponents/utilities/Avatar.vue';
import { Bar } from 'vue-chartjs';
import { Chart, Tooltip, BarElement, CategoryScale, LinearScale } from 'chart.js';
Chart.register(Tooltip, BarElement, CategoryScale, LinearScale);

import { createNamespacedHelpers } from 'vuex';

const report = createNamespacedHelpers('report');

export default {
    name: 'HorizontalBarChart',
    components: {
        Bar,
        ChipLabel,
        Avatar,
    },
    props: {
        data: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            options: null,
            activities: [
                { value: 'task', title: 'Tâche' },
                { value: 'event', title: 'Evènement' },
            ],
            checkbox: [],
            activitySelected: 'task',
        };
    },
    computed: {
        ...report.mapGetters(['staffs', 'type']),
        chartData() {
            const datasets = this.data.datasets.map((dataset) => ({
                ...dataset,
                data: dataset.data.map((count) => (!count ? null : count)),
                barThickness: 25,
                borderRadius: 100,
            }));
            return { ...this.data, datasets };
        },
        /**
         * Calculate the height of the barchart container
         */
        heightBarChart() {
            const height = this.data.labels.length * 24 * 2;
            return height < 400 ? 400 : height;
        },
        /**
         * Return an object nedeed to avatar component
         */
        avatar() {
            return (key) => {
                let avatar = {
                    type: 'initials',
                    value: ' ',
                    class: null,
                    fullname: ' ',
                };
                const id = key.replace('key_', '');
                const staff = this.staffs && this.staffs.find((staff) => staff.id === +id);
                if (staff) {
                    avatar = {
                        type: staff.avatar ? 'picture' : 'initials',
                        value: staff.avatar
                            ? staff.avatar
                            : staff.firstname.charAt(0) + staff.lastname.charAt(0) || ' ',
                        class: null,
                        fullname: `${staff.firstname || ''} ${staff.lastname || ''}`,
                    };
                }

                return avatar;
            };
        },
        isChartForLabel() {
            return this.type === 'label';
        },
    },
    watch: {
        data: function () {
            this.populateCheckedActivities();
        },
    },
    created() {
        this.setOptions();
        this.populateCheckedActivities();
    },
    methods: {
        /**
         * Manage the visibility of the bars of the graph and sort them accordingly
         */
        handleFilterLegendChange(isChecked, index) {
            const ci = this.$refs.barChart.chart;
            const meta = ci.getDatasetMeta(index);

            meta.hidden = !isChecked;

            ci.update();
        },
        /**
         * Set needed options for vertical bar chart
         */
        setOptions() {
            this.options = {
                indexAxis: 'y',
                responsive: true,
                maintainAspectRatio: false,
                font: {
                    family: "'Proxima Nova', 'Open Sans', sans-serif",
                    size: 14,
                    style: 'normal',
                },
                color: '#5C5C65',
                scales: {
                    x: {
                        // offset: true,
                        stacked: true,
                        beginAtZero: true,
                        position: 'top',
                        border: {
                            dash: [5, 5],
                        },
                        grid: {
                            display: true,
                            color: '#D8D8E6',
                            drawTicks: false,
                        },
                        ticks: {
                            display: true,
                            fontFamily: "'Proxima Nova', 'Open Sans', sans-serif",
                            fontSize: 14,
                            fontColor: '#5C5C65',
                            padding: 12,
                            beginAtZero: true,
                            callback: function (value, index, values) {
                                if (index !== 0 && Math.floor(value) === value) return value;
                            },
                        },
                    },
                    y: {
                        // offset: true,
                        position: 'left',
                        beginAtZero: true,
                        stacked: true,
                        display: false,
                    },
                },
                legend: {
                    display: false,
                },
                plugins: {
                    tooltip: {
                        mode: 'nearest',
                        callbacks: {
                            title: (tooltipItems) => {
                                const key = tooltipItems[0].label;
                                const title = this.avatar(key);
                                return title ? title.fullname : '';
                            },
                            label: (context) => {
                                const label = context.dataset.label || '';
                                const count = Math.round(context.raw * 100) / 100;
                                return `${label} : ${count}`;
                            },
                        },
                    },
                },
                animation: {
                    duration: 400,
                },
            };
        },
        onChangeActivitySelected() {
            this.$store.dispatch('report/updateActivitySelected', this.activitySelected);
        },
        populateCheckedActivities() {
            this.checkbox = [];
            this.data.datasets.forEach((_, index) => {
                this.checkbox.push(index);
            });
        },
    },
};
</script>

<style lang="scss" scoped>
@use '~@sellsy/styleguide/src/_variables' as *;

.list-label {
    max-height: calc(99vh - 500px);
}

.vertical-bar-chart {
    max-height: calc(99vh - 330px);
    .wrapper {
        display: grid;
        grid-template-columns: 24px 1fr;
        overflow-y: auto;
        gap: var(--spacing-1);
        &::-webkit-scrollbar {
            background: $white;
            width: 6px;
            display: block;
        }
        &::-webkit-scrollbar-thumb {
            background: $gray-60;
            border-radius: 6px;
        }
        // bar-labels-avatars
        .bar-labels-avatars {
            margin-top: 34px;
            justify-content: space-around;
            .avatars-wrapper {
                width: 24px;
            }
        }
    }
}
</style>

<style>
.horizontal-bar-chart-tool-tip {
    transform: translateX(-8.5%);
}
</style>
